import React, { useState } from 'react';
import Button from '../../../components/ui/Button';

const CompanySpecificPrep = () => {
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');

  const companies = [
    {
      id: 'google',
      name: 'Google',
      logo: '🟢',
      color: 'blue',
      positions: ['SDET', 'Test Engineer', 'QA Engineer'],
      difficulty: 'Hard',
      questions: 45,
      recentUpdates: '2 days ago'
    },
    {
      id: 'microsoft',
      name: 'Microsoft',
      logo: '🟦',
      color: 'indigo',
      positions: ['SDET', 'QA Engineer', 'Test Automation Engineer'],
      difficulty: 'Hard',
      questions: 52,
      recentUpdates: '1 week ago'
    },
    {
      id: 'amazon',
      name: 'Amazon',
      logo: '🟠',
      color: 'orange',
      positions: ['QA Engineer', 'Test Engineer', 'SDET'],
      difficulty: 'Medium-Hard',
      questions: 38,
      recentUpdates: '3 days ago'
    },
    {
      id: 'meta',
      name: 'Meta (Facebook)',
      logo: '🔵',
      color: 'blue',
      positions: ['Test Engineer', 'Mobile QA', 'Automation Engineer'],
      difficulty: 'Hard',
      questions: 41,
      recentUpdates: '5 days ago'
    },
    {
      id: 'apple',
      name: 'Apple',
      logo: '🍎',
      color: 'gray',
      positions: ['QA Engineer', 'Test Engineer', 'Automation Engineer'],
      difficulty: 'Medium-Hard',
      questions: 33,
      recentUpdates: '1 week ago'
    },
    {
      id: 'netflix',
      name: 'Netflix',
      logo: '🔴',
      color: 'red',
      positions: ['SDET', 'QA Engineer', 'Test Engineer'],
      difficulty: 'Medium',
      questions: 29,
      recentUpdates: '4 days ago'
    }
  ];

  const sampleQuestions = {
    google: [
      {
        question: "How would you test Google Search if it was 10x slower than expected?",
        type: "Problem Solving",
        difficulty: "Hard",
        tags: ["Performance", "System Design"]
      },
      {
        question: "Describe how you would approach testing a feature that works on 20+ different Android devices.",
        type: "Technical",
        difficulty: "Medium",
        tags: ["Mobile", "Compatibility"]
      },
      {
        question: "Tell me about a time when you disagreed with a developer about a bug priority.",
        type: "Behavioral",
        difficulty: "Medium",
        tags: ["Communication", "Leadership"]
      }
    ],
    microsoft: [
      {
        question: "How would you design an automation framework for testing Microsoft Office suite?",
        type: "System Design",
        difficulty: "Hard",
        tags: ["Architecture", "Automation"]
      },
      {
        question: "Explain your approach to testing accessibility features in software applications.",
        type: "Technical",
        difficulty: "Medium",
        tags: ["Accessibility", "Testing Strategy"]
      }
    ]
  };

  const interviewTips = {
    google: [
      "Focus on scalability and system thinking",
      "Prepare for coding questions in your preferred language",
      "Practice explaining complex technical concepts simply",
      "Be ready to discuss testing at massive scale"
    ],
    microsoft: [
      "Emphasize collaboration and teamwork",
      "Prepare for accessibility testing questions",
      "Know Microsoft\'s testing tools and practices",
      "Practice behavioral questions using STAR method"
    ],
    amazon: [
      "Study Amazon\'s Leadership Principles thoroughly",
      "Prepare customer-centric testing scenarios",
      "Practice system design for e-commerce platforms",
      "Be ready for bar-raiser rounds"
    ]
  };

  const getCompanyColor = (color) => {
    const colors = {
      blue: 'border-blue-500 bg-blue-50 hover:bg-blue-100 text-blue-900',
      indigo: 'border-indigo-500 bg-indigo-50 hover:bg-indigo-100 text-indigo-900',
      orange: 'border-orange-500 bg-orange-50 hover:bg-orange-100 text-orange-900',
      red: 'border-red-500 bg-red-50 hover:bg-red-100 text-red-900',
      gray: 'border-gray-500 bg-gray-50 hover:bg-gray-100 text-gray-900'
    };
    return colors?.[color] || colors?.blue;
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Easy': return 'text-green-700 bg-green-100 border border-green-300';
      case 'Medium': return 'text-blue-700 bg-blue-100 border border-blue-300';
      case 'Medium-Hard': return 'text-orange-700 bg-orange-100 border border-orange-300';
      case 'Hard': return 'text-red-700 bg-red-100 border border-red-300';
      default: return 'text-gray-700 bg-gray-100 border border-gray-300';
    }
  };

  const filteredQuestions = selectedCompany ? 
    sampleQuestions?.[selectedCompany]?.filter(q => 
      selectedDifficulty === 'all' || q?.difficulty === selectedDifficulty
    ) : [];

  if (selectedCompany) {
    const company = companies?.find(c => c?.id === selectedCompany);
    
    return (
      <div className="space-y-6">
        {/* Company Header - Enhanced colors */}
        <div className={`rounded-xl p-6 border-2 ${getCompanyColor(company?.color)}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-4xl">{company?.logo}</span>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{company?.name}</h2>
                <p className="text-gray-700 font-medium">Interview Preparation</p>
              </div>
            </div>
            <Button variant="outline" onClick={() => setSelectedCompany(null)}>
              Back to Companies
            </Button>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div className="text-center">
              <div className="text-lg font-bold text-gray-900">{company?.questions}</div>
              <div className="text-sm text-gray-700 font-medium">Questions</div>
            </div>
            <div className="text-center">
              <div className={`text-lg font-bold px-2 py-1 rounded ${getDifficultyColor(company?.difficulty)}`}>
                {company?.difficulty}
              </div>
              <div className="text-sm text-gray-700 font-medium">Difficulty</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-gray-900">{company?.positions?.length}</div>
              <div className="text-sm text-gray-700 font-medium">Positions</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-green-700">{company?.recentUpdates}</div>
              <div className="text-sm text-gray-700 font-medium">Updated</div>
            </div>
          </div>
        </div>

        {/* Difficulty Filter - Enhanced colors */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
          <div className="flex items-center space-x-4 mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Filter by Difficulty:</h3>
            <div className="flex space-x-2">
              {['all', 'Easy', 'Medium', 'Hard']?.map(difficulty => (
                <button
                  key={difficulty}
                  onClick={() => setSelectedDifficulty(difficulty)}
                  className={`
                    px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200
                    ${selectedDifficulty === difficulty
                      ? 'bg-blue-600 text-white shadow-md' 
                      : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                    }
                  `}
                >
                  {difficulty === 'all' ? 'All Levels' : difficulty}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Questions List - Enhanced readability */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
          <h3 className="text-xl font-semibold mb-4 text-gray-900">Interview Questions</h3>
          
          <div className="space-y-4">
            {filteredQuestions?.map((question, index) => (
              <div key={index} className="border border-gray-300 rounded-lg p-4 hover:shadow-md transition-shadow bg-white">
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-semibold text-gray-900 flex-1 pr-4">{question?.question}</h4>
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${getDifficultyColor(question?.difficulty)}`}>
                    {question?.difficulty}
                  </span>
                </div>
                
                <div className="flex items-center space-x-4 text-sm text-gray-700 mb-3">
                  <span className="font-semibold">{question?.type}</span>
                  <div className="flex space-x-1">
                    {question?.tags?.map((tag, tagIndex) => (
                      <span key={tagIndex} className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs font-medium">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div className="mt-3 flex space-x-2">
                  <Button size="sm" variant="outline">
                    Practice Answer
                  </Button>
                  <Button size="sm" variant="ghost">
                    View Solution
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Interview Tips - Improved text contrast */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
          <h3 className="text-xl font-semibold mb-4 text-gray-900">Interview Tips for {company?.name}</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold mb-3 text-gray-900">Preparation Tips</h4>
              <ul className="space-y-2 text-sm">
                {interviewTips?.[selectedCompany]?.map((tip, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <span className="text-blue-600 mt-0.5 font-bold">•</span>
                    <span className="text-gray-800">{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-3 text-gray-900">Common Positions</h4>
              <div className="space-y-2">
                {company?.positions?.map((position, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200">
                    <span className="font-semibold text-gray-900">{position}</span>
                    <Button size="xs" variant="outline">
                      View Details
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Company Grid - Enhanced colors */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {companies?.map((company) => (
          <div
            key={company?.id}
            onClick={() => setSelectedCompany(company?.id)}
            className={`
              p-6 rounded-xl border-2 cursor-pointer transition-all duration-300 hover:shadow-lg transform hover:scale-105 bg-white
              border-gray-300 hover:border-blue-400 text-gray-900
            `}
          >
            <div className="text-center">
              <span className="text-5xl mb-4 block">{company?.logo}</span>
              <h3 className="text-lg font-semibold mb-2 text-gray-900">{company?.name}</h3>
              
              <div className="space-y-2 mb-4">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-700 font-medium">Questions:</span>
                  <span className="font-semibold text-gray-900">{company?.questions}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-700 font-medium">Difficulty:</span>
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${getDifficultyColor(company?.difficulty)}`}>
                    {company?.difficulty}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-700 font-medium">Updated:</span>
                  <span className="text-green-700 font-semibold">{company?.recentUpdates}</span>
                </div>
              </div>

              <div className="mb-4">
                <h4 className="text-xs font-semibold text-gray-700 mb-2">OPEN POSITIONS</h4>
                <div className="flex flex-wrap gap-1 justify-center">
                  {company?.positions?.slice(0, 2)?.map((position, index) => (
                    <span key={index} className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs font-medium">
                      {position}
                    </span>
                  ))}
                  {company?.positions?.length > 2 && (
                    <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs font-medium">
                      +{company?.positions?.length - 2} more
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Success Stories - Enhanced text contrast */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
        <h3 className="text-xl font-semibold mb-4 text-gray-900">Success Stories</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            {
              name: "Sarah K.",
              company: "Google",
              position: "SDET",
              story: "Practiced 200+ Google-specific questions and got the offer! The behavioral prep was especially helpful.",
              avatar: "👩‍💻"
            },
            {
              name: "Mike R.",
              company: "Microsoft",
              position: "QA Engineer",
              story: "The company-specific tips helped me understand Microsoft's culture. Landed the job after 3 rounds.",
              avatar: "👨‍💻"
            }
          ]?.map((success, index) => (
            <div key={index} className="p-4 border border-gray-300 rounded-lg bg-white">
              <div className="flex items-center space-x-3 mb-3">
                <span className="text-2xl">{success?.avatar}</span>
                <div>
                  <h4 className="font-semibold text-gray-900">{success?.name}</h4>
                  <p className="text-sm text-gray-700">{success?.position} at {success?.company}</p>
                </div>
              </div>
              <p className="text-sm text-gray-800 italic font-medium">"{success?.story}"</p>
            </div>
          ))}
        </div>
      </div>

      {/* Statistics - Keep existing gradient with better text contrast */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl text-white p-6">
        <h3 className="text-xl font-semibold mb-4">Platform Statistics</h3>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold">1,247</div>
            <div className="text-sm text-blue-100 font-medium">Questions Available</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold">89%</div>
            <div className="text-sm text-blue-100 font-medium">Success Rate</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold">2.3k</div>
            <div className="text-sm text-blue-100 font-medium">Active Users</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold">45+</div>
            <div className="text-sm text-blue-100 font-medium">Companies</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanySpecificPrep;